import {
  Accordion,
  AccordionPanel,
  AccordionTrigger,
  Button,
  Flex,
  Text,
} from '@backstage/ui';
import { DetailDialog } from '@internal/ui-common';
import type { Entity } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';

import { CheckConditions } from '../CheckConditions/CheckConditions';
import { CheckResultBadge } from '../CheckResultBadge/CheckResultBadge';
import { toCheckResultDisplay } from '../CheckResultBadge/checkResult';
import type { CheckRow } from '../../hooks/useEntityScorecard';
import { FactValueFindings } from '../FactValueFindings/FactValueFindings';
import { useEffect, useRef, useState } from 'react';
import styles from './CheckDetailDialog.module.css';

function resolveProjectUrl(entity: Entity): string | undefined {
  const location =
    entity.metadata.annotations?.['backstage.io/managed-by-location'] ?? '';
  const match = location.match(/^url:(https?:\/\/.+?)\/-\/blob\//);
  return match?.[1];
}

interface CheckDetailDialogProps {
  row: CheckRow | null;
  onClose: () => void;
}

const JsonBlock = ({ value }: { value: unknown }) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const json = JSON.stringify(value, null, 2);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      clearTimeout(timeoutRef.current);
      setCopied(true);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard write denied — button stays in "Copy" state
    }
  };

  return (
    <div className={styles.jsonBlock}>
      <div className={styles.copyButton}>
        <Button size="small" variant="secondary" onPress={handleCopy}>
          {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>
      <pre className={styles.jsonPre}>{json}</pre>
    </div>
  );
};

export const CheckDetailDialog = ({ row, onClose }: CheckDetailDialogProps) => {
  const { entity } = useEntity();
  const projectUrl = resolveProjectUrl(entity);

  if (!row) return null;

  const { check, snapshot, findings } = row;

  const footer = projectUrl ? (
    <Button
      size="small"
      variant="secondary"
      onPress={() => window.open(projectUrl, '_blank', 'noreferrer')}
    >
      View in GitLab
    </Button>
  ) : undefined;

  return (
    <DetailDialog
      isOpen={!!row}
      onClose={onClose}
      title={check.name}
      footer={footer}
    >
      <div className={styles.content}>
        {check.description && (
          <Text as="p" variant="body-large" color="secondary">
            {check.description}
          </Text>
        )}

        {snapshot && (
          <Flex align="center" gap="2">
            <CheckResultBadge result={toCheckResultDisplay(snapshot)} />
            <Text as="span" variant="body-medium" color="secondary">
              Last checked: {new Date(snapshot.snapshotTime).toLocaleString()}
            </Text>
          </Flex>
        )}

        <CheckConditions checkJson={check.checkJson} />

        {snapshot?.result === 'failed' && findings.length > 0 && (
          <div>
            <FactValueFindings findings={findings} />
          </div>
        )}

        {snapshot?.result === 'failed' && findings.length === 0 && (
          <Text as="p" color="secondary" variant="body-medium">
            Check failed. No fact data is available for this result.
          </Text>
        )}

        {snapshot?.result === 'passed' && (
          <Text as="p" color="success" variant="body-medium">
            All checks passed.
          </Text>
        )}

        {!snapshot && (
          <Text as="p" color="secondary" variant="body-medium">
            This check has not run for this entity yet.
          </Text>
        )}

        {!!snapshot?.factValue && (
          <Accordion>
            <AccordionTrigger level={6}>
              <Text as="span" variant="body-medium" weight="bold">
                Raw Evidence
              </Text>
            </AccordionTrigger>
            <AccordionPanel>
              <JsonBlock value={snapshot.factValue} />
            </AccordionPanel>
          </Accordion>
        )}
      </div>
    </DetailDialog>
  );
};
